import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { LuX } from 'react-icons/lu';
import '../../../styles/wizard-rt3.css';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Shared onboarding dialog shell.
 *
 * `onClose` is wired to backdrop click, Escape and the close button only when
 * `dismissible` is true, so callers keep full control over which interactions
 * are allowed to dismiss (matching each dialog's previous behaviour).
 */
const OnboardingModal = ({
  open = true,
  title,
  description,
  icon,
  tone = 'primary',
  size = 'md',
  align = 'start',
  onClose,
  dismissible = true,
  showClose = dismissible,
  busy = false,
  footer,
  children,
  className = '',
  initialFocusRef,
}) => {
  const panelRef = useRef(null);
  const titleId = useId();
  const descId = useId();
  const canDismiss = dismissible && !busy && typeof onClose === 'function';

  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement;
    const panel = panelRef.current;
    const target =
      initialFocusRef?.current ||
      panel?.querySelector('[data-autofocus]') ||
      panel?.querySelector('.ob-modal__footer .ob-btn:not(:disabled)') ||
      panel;
    target?.focus?.({ preventScroll: true });

    return () => {
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [open, initialFocusRef]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if ((event.key === 'Escape' || event.key === 'Esc') && !event.defaultPrevented) {
        if (canDismiss) {
          event.preventDefault();
          onClose();
        }
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;
      const nodes = Array.from(panelRef.current.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (!nodes.length) {
        event.preventDefault();
        panelRef.current.focus();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, canDismiss, onClose]);

  if (!open) return null;

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget && canDismiss) onClose();
  };

  const panelClass = [
    'ob-modal',
    `ob-modal--${size}`,
    align === 'center' ? 'ob-modal--center' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <div
      className="ob-modal-root"
      onClick={handleBackdropClick}
      {...(canDismiss ? { 'data-rt-modal-overlay': true } : {})}
    >
      <div
        ref={panelRef}
        className={panelClass}
        role={tone === 'danger' || tone === 'warning' ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descId : undefined}
        aria-busy={busy || undefined}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || icon) && (
          <div className="ob-modal__header">
            {icon && <span className={`ob-modal__icon ob-modal__icon--${tone}`} aria-hidden="true">{icon}</span>}
            <div className="ob-modal__heading">
              {title && <h2 id={titleId} className="ob-modal__title">{title}</h2>}
              {description && <p id={descId} className="ob-modal__desc">{description}</p>}
            </div>
          </div>
        )}
        {showClose && canDismiss && (
          <button type="button" className="ob-modal__close" aria-label="Close" onClick={onClose}>
            <LuX size={18} />
          </button>
        )}
        {children && <div className="ob-modal__body">{children}</div>}
        {footer && <div className="ob-modal__footer">{footer}</div>}
      </div>
    </div>
  );

  if (typeof document === 'undefined') return content;
  return createPortal(content, document.body);
};

export default OnboardingModal;
